import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { API } from "@/lib/api";
import { PageHeader } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { CreditCard, Download, CheckCircle2, Clock, FileText, Sparkles, CalendarPlus } from "lucide-react";
import { toast } from "sonner";

const PLAN_LABEL = (p) => ({ MONTHLY: "1 Month", SIX_MONTH: "6 Months", ANNUAL: "12 Months (Annual)" }[p] || p || "—");
const ngn = (v, c = "NGN") => (v == null ? "—" : `${c === "NGN" ? "\u20a6" : ""}${Number(v).toLocaleString()}`);

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-slate-100 last:border-0 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900 text-right" data-testid={testid}>{value ?? "—"}</span>
    </div>
  );
}

export default function Billing() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [selTerm, setSelTerm] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [purchasing, setPurchasing] = useState(false);

  const selectTerm = async (term) => {
    setSelTerm(term);
    setQuote(null);
    setQuoting(true);
    try {
      const { data } = await api.post("/payments/subscription/quote", { term });
      setQuote(data);
    } catch (e) {
      toast.error(apiError(e));
      setSelTerm(null);
    } finally {
      setQuoting(false);
    }
  };

  const proceedToPayment = async () => {
    if (!selTerm) return;
    setPurchasing(true);
    try {
      const { data } = await api.post("/payments/subscription/purchase", {
        term: selTerm,
        callback_url: `${window.location.origin}/app/billing`,
      });
      const url = data?.authorization_url || data?.authorization?.authorization_url;
      if (url) window.location.href = url;
      else { toast.error("Could not start payment. Contact info@epd-nigeria.com."); setPurchasing(false); }
    } catch (e) {
      toast.error(apiError(e));
      setPurchasing(false);
    }
  };

  const load = () =>
    api.get("/payments/billing")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    // Returning from Paystack checkout — refetch so activation (via webhook) is reflected.
    const p = new URLSearchParams(window.location.search);
    if (p.get("reference") || p.get("trxref")) {
      const t = setTimeout(load, 2500);
      return () => clearTimeout(t);
    }
  }, []);

  const startPayment = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/paystack/initialize", {
        callback_url: `${window.location.origin}/app/billing`,
      });
      const url = data?.authorization_url || data?.authorization?.authorization_url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error("Could not start payment. Please try again or contact info@epd-nigeria.com.");
        setPaying(false);
      }
    } catch (e) {
      toast.error(apiError(e));
      setPaying(false);
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!data) return <p className="text-sm text-muted-foreground">No billing information available.</p>;

  const s = data.subscription || {};
  const legacy = data.legacy_migrated;
  const active = data.commercial_state === "SUBSCRIPTION_ACTIVE" || s.subscription_status === "ACTIVE";
  const pending = data.commercial_state === "PAYMENT_PENDING";
  const providerConfigured = (data.provider_status || "").startsWith("CONFIGURED");
  const date = (d) => (d ? String(d).slice(0, 10) : "—");

  return (
    <div data-testid="billing-page">
      <PageHeader title="Subscription & Billing" subtitle="Your EPD Nigeria subscription, payment and invoices." />

      {/* Status banner */}
      <div className="bg-white border rounded-lg p-5 mb-5">
        {legacy ? (
          <div className="flex items-center gap-2 text-slate-700" data-testid="billing-status-legacy">
            <CheckCircle2 className="h-5 w-5 text-slate-500" />
            <span className="font-medium">Migrated account</span>
            <span className="text-sm text-slate-500">— your workspace access is active.</span>
          </div>
        ) : active ? (
          <div className="flex items-center gap-2 text-emerald-700" data-testid="billing-status-active">
            <CheckCircle2 className="h-5 w-5" />
            <span className="font-medium">Subscription active</span>
          </div>
        ) : pending ? (
          <div data-testid="billing-status-pending">
            <div className="flex items-center gap-2 text-amber-700">
              <Clock className="h-5 w-5" />
              <span className="font-medium">Payment pending</span>
            </div>
            <p className="text-sm text-slate-600 mt-2">
              Selected plan <b>{PLAN_LABEL(s.plan)}</b> · <b>{s.contracted_named_users}</b> named user(s) ·
              expected subtotal <b>{ngn(s.subtotal, s.currency)}</b>.
            </p>
            {!providerConfigured && (
              <p className="text-sm text-slate-500 mt-2" data-testid="billing-provider-unconfigured">
                Online payment is not yet configured. Our team will be in touch to complete activation.
              </p>
            )}
            {providerConfigured && (
              <Button className="mt-4" onClick={startPayment} disabled={paying}
                      data-testid="billing-pay-now-btn">
                <CreditCard className="h-4 w-4 mr-2" />
                {paying ? "Redirecting to Paystack…" : `Pay ${ngn(s.subtotal, s.currency)} now`}
              </Button>
            )}
          </div>
        ) : (
          <div className="text-sm text-slate-600" data-testid="billing-status-other">
            Commercial status: <b>{data.commercial_state || "—"}</b>
          </div>
        )}
      </div>

      {/* Manage subscription — buy additional months (advance renewal / upgrade); org_admin only */}
      {!legacy && (
        <div className="bg-white border rounded-lg p-5 mb-5" data-testid="manage-subscription">
          <div className="flex items-center gap-2 mb-1">
            <CalendarPlus className="h-4 w-4 text-slate-400" />
            <h3 className="font-medium">Manage subscription</h3>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            Add more subscription time for your <b>{s.contracted_named_users || 1}</b> named user(s).
            Extra time stacks after your current entitlement — you never lose unused paid time.
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            {(data.terms || []).map((t) => (
              <button
                key={t.term}
                type="button"
                onClick={() => selectTerm(t.term)}
                data-testid={`term-card-${t.term}`}
                className={`text-left rounded-lg border p-4 transition-colors ${
                  selTerm === t.term ? "border-emerald-500 ring-1 ring-emerald-500 bg-emerald-50/40" : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-900">{t.label}</span>
                  {t.best_value && (
                    <span className="inline-flex items-center gap-1 text-[11px] rounded-full bg-amber-100 text-amber-800 px-2 py-0.5" data-testid="term-best-value">
                      <Sparkles className="h-3 w-3" /> Best value — 2 months included
                    </span>
                  )}
                </div>
                <div className="mt-2 text-lg font-semibold text-slate-900">{ngn(t.unit_price, t.currency)}</div>
                <div className="text-xs text-slate-500">per named user · {t.months} month(s)</div>
              </button>
            ))}
          </div>

          {quoting && <p className="text-sm text-slate-500 mt-4">Calculating…</p>}
          {quote && !quoting && (
            <div className="mt-5 border-t pt-4" data-testid="checkout-preview">
              <h4 className="font-medium mb-2 text-sm">Checkout summary</h4>
              <div className="max-w-md">
                <Row label="Selected term" value={PLAN_LABEL(quote.term)} testid="co-term" />
                <Row label="Named users" value={quote.contracted_named_users} testid="co-users" />
                <Row label="Unit price" value={ngn(quote.unit_price, quote.currency)} testid="co-unit" />
                <Row label="Subtotal" value={ngn(quote.subtotal, quote.currency)} testid="co-subtotal" />
                <Row label="Current subscription expiry" value={date(quote.current_expiry)} testid="co-current-expiry" />
                <Row label="Additional period" value={`${quote.months} month(s)`} testid="co-period" />
                <Row label="Projected new expiry" value={date(quote.projected_new_expiry)} testid="co-new-expiry" />
                <Row label="Amount payable" value={ngn(quote.amount_ngn, quote.currency)} testid="co-amount" />
              </div>
              <Button className="mt-4" onClick={proceedToPayment} disabled={purchasing || !providerConfigured}
                      data-testid="proceed-to-payment-btn">
                <CreditCard className="h-4 w-4 mr-2" />
                {purchasing ? "Redirecting to Paystack…" : `Proceed to payment · ${ngn(quote.amount_ngn, quote.currency)}`}
              </Button>
              {!providerConfigured && (
                <p className="text-xs text-slate-500 mt-2">Online payment is not yet configured.</p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        <div className="bg-white border rounded-lg p-5">
          <h3 className="font-medium mb-3 flex items-center gap-2"><CreditCard className="h-4 w-4 text-slate-400" /> Subscription</h3>
          <Row label="Commercial status" value={data.commercial_state} testid="bill-commercial-state" />
          <Row label="Subscription status" value={s.subscription_status} testid="bill-sub-status" />
          <Row label="Payment status" value={legacy ? "Migrated account" : s.payment_status} testid="bill-payment-status" />
          <Row label="Plan" value={PLAN_LABEL(s.plan)} testid="bill-plan" />
          <Row label="Contracted named users" value={s.contracted_named_users} testid="bill-seats" />
          <Row label="Active named users" value={s.active_named_users} testid="bill-active-seats" />
          <Row label="Unit price" value={ngn(s.unit_price, s.currency)} />
          <Row label="Current effective unit price" value={`${ngn(s.unit_price, s.currency)} / user`} testid="bill-effective-unit" />
          <Row label="Paid-through date" value={date(data.paid_through || s.expires_at)} testid="bill-paid-through" />
          <Row label="Subtotal" value={ngn(s.subtotal, s.currency)} testid="bill-subtotal" />
          <Row label="Total" value={ngn(s.total_amount ?? s.subtotal, s.currency)} testid="bill-total" />
        </div>

        <div className="bg-white border rounded-lg p-5">
          <h3 className="font-medium mb-3 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-slate-400" /> Term & payment</h3>
          <Row label="Subscription start" value={date(s.starts_at)} testid="bill-start" />
          <Row label="Subscription expiry" value={date(s.expires_at)} testid="bill-expiry" />
          <Row label="Payment provider" value={s.provider || "—"} />
          <Row label="Payment method" value={s.payment_method === "BANK_TRANSFER" ? "Bank Transfer" : s.payment_method === "CARD" ? "Card" : "—"} testid="bill-payment-method" />
          <Row label="Transaction reference" value={s.provider_transaction_reference || "—"} testid="bill-txn-ref" />
        </div>
      </div>

      {/* Invoices */}
      <div className="bg-white border rounded-lg overflow-hidden mt-5" data-testid="invoice-list">
        <div className="px-5 py-3 border-b"><h3 className="font-medium flex items-center gap-2"><FileText className="h-4 w-4 text-slate-400" /> Invoices</h3></div>
        <table className="w-full text-sm epn-table">
          <thead className="bg-slate-50 border-b"><tr className="text-left text-xs text-slate-500">
            <th className="px-5 py-2.5">Invoice</th><th className="px-5 py-2.5">Date</th>
            <th className="px-5 py-2.5">Amount</th><th className="px-5 py-2.5">Status</th>
            <th className="px-5 py-2.5 text-right">Action</th>
          </tr></thead>
          <tbody>
            {(data.invoices || []).length === 0 && (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-muted-foreground">No invoices yet.</td></tr>
            )}
            {(data.invoices || []).map((inv) => (
              <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className="border-b last:border-0">
                <td className="px-5 py-3 font-mono text-xs">{inv.invoice_number || "—"}</td>
                <td className="px-5 py-3 text-xs">{inv.date}</td>
                <td className="px-5 py-3">{ngn(inv.amount, inv.currency)}</td>
                <td className="px-5 py-3">
                  {inv.status === "CONFIGURATION_INCOMPLETE" ? (
                    <span className="text-xs rounded-full border bg-amber-50 border-amber-200 text-amber-700 px-2.5 py-0.5" data-testid={`invoice-pending-${inv.id}`}>Invoice pending configuration</span>
                  ) : (
                    <span className="text-xs rounded-full border bg-emerald-50 border-emerald-200 text-emerald-700 px-2.5 py-0.5">{inv.status}</span>
                  )}
                </td>
                <td className="px-5 py-3 text-right">
                  {inv.downloadable ? (
                    <a href={`${API}/payments/invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer"
                       data-testid={`invoice-download-${inv.id}`}>
                      <Button size="sm" variant="outline"><Download className="h-3.5 w-3.5 mr-1.5" /> Download</Button>
                    </a>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Subscription history (immutable extension transactions) */}
      {(data.transactions || []).length > 0 && (
        <div className="bg-white border rounded-lg overflow-hidden mt-5" data-testid="subscription-history">
          <div className="px-5 py-3 border-b"><h3 className="font-medium flex items-center gap-2"><CalendarPlus className="h-4 w-4 text-slate-400" /> Subscription history</h3></div>
          <table className="w-full text-sm epn-table">
            <thead className="bg-slate-50 border-b"><tr className="text-left text-xs text-slate-500">
              <th className="px-5 py-2.5">Term</th><th className="px-5 py-2.5">Users</th>
              <th className="px-5 py-2.5">Amount</th><th className="px-5 py-2.5">From</th>
              <th className="px-5 py-2.5">New expiry</th>
            </tr></thead>
            <tbody>
              {(data.transactions || []).map((t) => (
                <tr key={t.id} data-testid={`txn-row-${t.id}`} className="border-b last:border-0">
                  <td className="px-5 py-3">{PLAN_LABEL(t.term)}</td>
                  <td className="px-5 py-3">{t.contracted_named_users}</td>
                  <td className="px-5 py-3">{ngn(t.subtotal, t.currency)}</td>
                  <td className="px-5 py-3 text-xs">{date(t.effective_from)}</td>
                  <td className="px-5 py-3 text-xs">{date(t.effective_to)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
